import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface HierarchyNode {
  label: string;
  description?: string;
  children?: HierarchyNode[];
  color?: string;
  icon?: string;
}

interface HierarchyVisualizationProps {
  data: HierarchyNode;
  config: {
    title?: string;
    description?: string;
    explanation?: string;
    collapsible?: boolean;
  };
  preview?: boolean;
  collapsed?: boolean;
}

const DEPTH_COLORS = [
  'var(--primary-color)',
  'hsl(var(--chart-6))',
  'hsl(var(--chart-7))',
  'hsl(var(--chart-8))',
  'hsl(var(--chart-9))',
  'hsl(var(--chart-10))',
];

const HierarchyNodeComponent: React.FC<{
  node: HierarchyNode;
  depth: number;
  collapsible: boolean;
  preview: boolean;
}> = ({ node, depth, collapsible, preview }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const color = node.color || DEPTH_COLORS[depth % DEPTH_COLORS.length];

  return (
    <div className="viz-hierarchy-node-group">
      <div
        className={`viz-hierarchy-node ${hasChildren ? 'viz-hierarchy-node-parent' : 'viz-hierarchy-node-leaf'}`}
        style={{ ['--depth-color' as any]: color }}
        onClick={collapsible && hasChildren ? () => setExpanded(!expanded) : undefined}
      >
        {hasChildren && collapsible && (
          <span className={`viz-hierarchy-toggle ${expanded ? 'viz-hierarchy-toggle-open' : ''}`}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M3 1L7 5L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
        <div className="viz-hierarchy-dot" style={{ backgroundColor: color }} />
        <div className="viz-hierarchy-node-content">
          <span className="viz-hierarchy-label">{node.label}</span>
          {node.description && !preview && (
            <span className="viz-hierarchy-desc">{node.description}</span>
          )}
        </div>
      </div>
      {hasChildren && expanded && (
        <div className="viz-hierarchy-children" style={{ borderColor: `${color}33` }}>
          {node.children!.map((child, index) => (
            <HierarchyNodeComponent
              key={index}
              node={child}
              depth={depth + 1}
              collapsible={collapsible}
              preview={preview}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const HierarchyVisualization: React.FC<HierarchyVisualizationProps> = ({
  data,
  config,
  preview = false,
  collapsed = false,
}) => {
  if (!data) {
    return <div className="viz-unsupported">No hierarchy data provided</div>;
  }

  const collapsible = config.collapsible !== false;

  return (
    <div className={`viz-hierarchy-wrapper ${preview ? 'viz-preview' : ''}`}>
      {!preview && config.title && (
        <div className="viz-hierarchy-header">
          <h3 className="viz-hierarchy-title">{config.title}</h3>
          {config.description && <p className="viz-hierarchy-description">{config.description}</p>}
        </div>
      )}
      <motion.div
        initial={false}
        animate={{
          height: collapsed ? 0 : 'auto',
          opacity: collapsed ? 0 : 1,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: 'easeOut' },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className="viz-hierarchy">
          <HierarchyNodeComponent
            node={data}
            depth={0}
            collapsible={collapsible}
            preview={preview}
          />
        </div>
        {!preview && config.explanation && (
          <div className="viz-hierarchy-footer">
            <span>{config.explanation}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default HierarchyVisualization;
